let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Dimensions,
  Alert,
  Modal,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Ionicons,
  FontAwesome5,
  MaterialIcons,
  MaterialCommunityIcons,
  AntDesign,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width } = Dimensions.get('window');

// Default company logo placeholder
const defaultLogo = {
  uri: 'https://via.placeholder.com/100/0e4e96/FFFFFF?text=Company',
};

// ─────────────────────────────────────────────────────────────────────────────
// RescheduleService — writes to the live `application_interviews` table via
// the same /api/data PUT pattern VideoInterviewScreen uses. AsyncStorage is
// kept only as a best-effort offline echo so the UI feels instant; the
// source of truth is always the database.
// ─────────────────────────────────────────────────────────────────────────────
const RescheduleService = {
  // Combines a JS Date (calendar day) + a "10:00 AM" style slot label into a
  // single ISO timestamp. The recruiter side stores everything as UTC ISO
  // so they can compare proposed vs. scheduled cleanly.
  _combineDateAndSlot(date, slotLabel) {
    if (!date || !slotLabel) return null;
    const m = String(slotLabel).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const period = (m[3] || '').toUpperCase();
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    const d = new Date(date);
    d.setHours(h, min, 0, 0);
    return isNaN(d.getTime()) ? null : d.toISOString();
  },

  async saveRescheduleRequest(interviewId, rescheduleData) {
    if (!interviewId) throw new Error('Missing interviewId');
    console.log('💾 Saving reschedule request to DB:', { interviewId, rescheduleData });

    const nowIso = new Date().toISOString();
    const proposedAt = this._combineDateAndSlot(
      rescheduleData.newDateObj || rescheduleData.newDate,
      rescheduleData.newTime,
    );

    // Real DB columns — keeps the same shape VideoInterviewScreen's
    // normalizer/patchInterview agree on, plus structured proposed time
    // fields the recruiter side can act on.
    const payload = {
      status: 'reschedule_requested',
      candidate_reschedule_reason:        rescheduleData.reason || '',
      candidate_reschedule_message:       rescheduleData.message || '',
      candidate_preferred_times:          [rescheduleData.newDate, rescheduleData.newTime].filter(Boolean).join(' · '),
      candidate_proposed_at:              proposedAt,
      candidate_proposed_date:            rescheduleData.newDate || null,
      candidate_proposed_time:            rescheduleData.newTime || null,
      candidate_reschedule_requested_at:  nowIso,
    };

    const res = await apiFetch(`/api/data/application_interviews/${interviewId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (res?.error) throw new Error(res.error);

    // Offline echo — same key shape as before so any existing readers keep
    // working, but it is no longer the source of truth.
    const requestData = {
      ...rescheduleData,
      proposedAt,
      requestId: `req_${Date.now()}`,
      status: 'pending',
      requestedAt: nowIso,
      interviewId,
    };
    try { await AsyncStorage.setItem(`reschedule_${interviewId}`, JSON.stringify(requestData)); } catch {}

    return requestData;
  },

  async getRescheduleHistory(interviewId) {
    if (!interviewId) return null;
    // Prefer DB; fall back to cached AsyncStorage echo if the request fails
    // (offline / transient API error). Returning null means "no pending
    // request", which is treated by the UI as a clean state.
    try {
      const row = await apiFetch(`/api/data/application_interviews/${interviewId}`);
      if (row && !row.error) {
        const r = Array.isArray(row) ? row[0] : (row.data || row);
        if (r && String(r.status || '').toLowerCase() === 'reschedule_requested') {
          return {
            status:        'pending',
            reason:        r.candidate_reschedule_reason || '',
            message:       r.candidate_reschedule_message || '',
            newDate:       r.candidate_proposed_date || '',
            newTime:       r.candidate_proposed_time || '',
            proposedAt:    r.candidate_proposed_at || null,
            requestedAt:   r.candidate_reschedule_requested_at || null,
            interviewId,
          };
        }
        return null;
      }
    } catch (e) {
      console.warn('getRescheduleHistory: DB read failed, falling back to cache:', e?.message);
    }
    try {
      const cached = await AsyncStorage.getItem(`reschedule_${interviewId}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  },

  async getAvailableSlots(date) {
    // TODO: Replace with a real /api/data/interviewer_availability endpoint
    // when the recruiter side exposes one. For now, candidate proposes a
    // time and recruiter confirms or counter-proposes on their side, so a
    // sensible default set of business-hours slots is fine.
    const slots = [
      { id: '1', time: '09:00 AM', available: true },
      { id: '2', time: '10:00 AM', available: true },
      { id: '3', time: '11:00 AM', available: true },
      { id: '4', time: '01:00 PM', available: true },
      { id: '5', time: '02:00 PM', available: true },
      { id: '6', time: '03:00 PM', available: true },
      { id: '7', time: '04:00 PM', available: true },
      { id: '8', time: '05:00 PM', available: true },
    ];
    const dow = date.getDay();
    return slots.map(s => ({ ...s, available: s.available && dow !== 0 && dow !== 6 }));
  },
};

export default function RescheduleInterviewScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    // Prefetch the latest state of THIS interview so the screen reflects
    // any recruiter-side updates (e.g. status flipped, scheduled_at moved)
    // since the calling screen last loaded.
    const id = interviewId || (interview && (interview.id || interview.interview_id));
    if (!id) return;
    const _ck = `professional_${_profUserId}_rescheduleinterview_${id}`;
    apiFetch(`/api/data/application_interviews/${id}`).then(d => {
      if (d && !d.error) {
        const row = Array.isArray(d) ? d[0] : (d.data || d);
        if (row) AsyncStorage.setItem(_ck, JSON.stringify(row)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId, interviewId, interview]);

  const navigation = useNavigation();
  const route = useRoute();
  
  // ** INTEGRATION: Enhanced parameter extraction **
  const { 
    interview, 
    interviewId, 
    source = '',
    returnScreen = 'TestsInterviews' 
  } = route.params || {};

  // States for date and time selection
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rescheduleData, setRescheduleData] = useState(null);

  // ** INTEGRATION: Enhanced interview details — accepts both the legacy
  // flat shape (date/time/duration strings) AND the camelCase shape
  // VideoInterviewScreen passes through (scheduledAt ISO, durationMinutes,
  // position, company, companyLogo). The screen displays a date+time string
  // regardless, so we normalize once here.
  const iv = interview || {};
  const _fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const _fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };
  const interviewDetails = {
    id:        iv.id || interviewId || '',
    company:   iv.company || iv.employer_name || iv.company_name || 'Company',
    position:  iv.position || iv.job_title || iv.title || 'Interview',
    type:      iv.type
                 ? String(iv.type).replace('_', ' ')
                 : (iv.interview_type || 'Video Interview'),
    date:      iv.date || (iv.scheduledAt ? _fmtDate(iv.scheduledAt) : ''),
    time:      iv.time || (iv.scheduledAt ? _fmtTime(iv.scheduledAt) : ''),
    duration:  iv.duration
                 || (iv.durationMinutes ? `${iv.durationMinutes} min` : '45 min'),
    logo:      iv.logo
                 || (iv.companyLogo ? { uri: iv.companyLogo } : defaultLogo),
    // Original ISO kept around in case the recruiter API needs it later.
    scheduledAt: iv.scheduledAt || iv.scheduled_at || null,
  };

  // Sample available dates (next 14 days, excluding weekends)
  const availableDates = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i + 1); // Start from tomorrow
    return date;
  }).filter(date => {
    const dayOfWeek = date.getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclude weekends
  }).slice(0, 10); // Take first 10 weekdays

  // Sample reasons for rescheduling
  const rescheduleReasons = [
    'Schedule conflict',
    'Need more preparation time',
    'Personal emergency',
    'Technical issues',
    'Medical appointment',
    'Travel conflict',
    'Other (please specify)',
  ];

  useEffect(() => {
    initializeScreen();
  }, []);

  useEffect(() => {
    loadTimeSlots();
  }, [selectedDate]);

  // ** INTEGRATION: Enhanced initialization **
  const initializeScreen = async () => {
    try {
      setLoading(true);
      
      // Load any existing reschedule data
      const existingReschedule = await RescheduleService.getRescheduleHistory(interviewDetails.id);
      if (existingReschedule) {
        setRescheduleData(existingReschedule);
        console.log('📋 Found existing reschedule request:', existingReschedule);
      }
      
      // Set initial date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow);
      
      // Log source information
      if (source) {
        console.log(`🔗 Navigated from ${source} to RescheduleInterview`);
      }
      
    } catch (error) {
      console.error('❌ Error initializing reschedule screen:', error);
    } finally {
      setLoading(false);
    }
  };

  // ** INTEGRATION: Enhanced time slot loading **
  const loadTimeSlots = async () => {
    try {
      console.log('🔄 Loading time slots for:', selectedDate);
      const slots = await RescheduleService.getAvailableSlots(selectedDate);
      setTimeSlots(slots);
      setSelectedTimeSlot(null); // Reset selection when date changes
    } catch (error) {
      console.error('❌ Error loading time slots:', error);
      setTimeSlots([]);
    }
  };

  // Custom date picker handlers
  const toggleCustomDatePicker = () => {
    setShowCustomDatePicker(!showCustomDatePicker);
  };

  const handleDateSelect = date => {
    setSelectedDate(date);
    setShowCustomDatePicker(false);
  };

  // Format date for display
  const formatDate = date => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-GB', options);
  };

  // Format date for custom picker display
  const formatDateShort = date => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('en-GB', options);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = slot => {
    if (slot.available) {
      setSelectedTimeSlot(slot);
    }
  };

  // Handle reason selection
  const handleReasonSelect = selectedReason => {
    setReason(selectedReason);
  };

  // ** INTEGRATION: Enhanced submit with real functionality **
  const handleSubmit = async () => {
    if (!selectedDate || !selectedTimeSlot) {
      Alert.alert(
        'Missing Information',
        'Please select both date and time for your interview.'
      );
      return;
    }

    if (!reason) {
      Alert.alert(
        'Missing Information',
        'Please provide a reason for rescheduling.'
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare reschedule data — include the raw Date object so the
      // service can build a proper ISO `candidate_proposed_at` timestamp
      // for the recruiter to act on.
      const rescheduleRequestData = {
        originalDate: interviewDetails.date,
        originalTime: interviewDetails.time,
        newDate:    formatDate(selectedDate),
        newDateObj: selectedDate,
        newTime:    selectedTimeSlot.time,
        reason:     reason,
        message:    message,
        interviewId: interviewDetails.id,
        company:    interviewDetails.company,
        position:   interviewDetails.position,
      };

      // Save reschedule request
      const savedRequest = await RescheduleService.saveRescheduleRequest(
        interviewDetails.id, 
        rescheduleRequestData
      );

      console.log('✅ Reschedule request saved:', savedRequest);

      // Show confirmation modal
      setShowConfirmationModal(true);

    } catch (error) {
      console.error('❌ Error submitting reschedule request:', error);
      Alert.alert(
        'Error',
        'Failed to submit reschedule request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ** INTEGRATION: Enhanced confirmation with navigation **
  const handleConfirmation = () => {
    setShowConfirmationModal(false);
    
    // Navigate back with update information
    if (source === 'InterviewDetails') {
      navigation.navigate('InterviewDetails', {
        interview: {
          ...interviewDetails,
          status: 'reschedule_requested'
        },
        source: 'RescheduleInterview',
        refreshData: true
      });
    } else if (source === 'InterviewHistory') {
      navigation.navigate('InterviewHistory', {
        refreshData: true,
        updatedInterviewId: interviewDetails.id
      });
    } else {
      navigation.navigate(returnScreen, {
        refreshData: true,
        tab: 'interviews'
      });
    }
  };

  // Custom Date Picker Component
  const CustomDatePicker = () => (
    <Modal
      visible={showCustomDatePicker}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowCustomDatePicker(false)}
    >
      <View style={styles.datePickerModalContainer}>
        <View style={styles.datePickerModalContent}>
          <View style={styles.datePickerHeader}>
            <Text style={styles.datePickerTitle}>Select Date</Text>
            <TouchableOpacity onPress={() => setShowCustomDatePicker(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.datePickerScrollView}
          >
            {availableDates.map((date, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.datePickerItem,
                  selectedDate.toDateString() === date.toDateString() &&
                    styles.datePickerItemSelected,
                ]}
                onPress={() => handleDateSelect(date)}
              >
                <Text
                  style={[
                    styles.datePickerDayText,
                    selectedDate.toDateString() === date.toDateString() &&
                      styles.datePickerTextSelected,
                  ]}
                >
                  {formatDateShort(date)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity
            style={styles.datePickerConfirmButton}
            onPress={() => setShowCustomDatePicker(false)}
          >
            <Text style={styles.datePickerConfirmText}>Confirm</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // Render loading state
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Reschedule Interview</Text>
          <View style={{ width: 32 }}>{/* Empty view for balance */}</View>
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading reschedule options...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Reschedule Interview</Text>
        <View style={{ width: 32 }}>{/* Empty view for balance */}</View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        {/* Source indicator */}
        {source && (
          <View style={styles.sourceIndicator}>
            <Ionicons name="information-circle-outline" size={16} color="#007AFF" />
            <Text style={styles.sourceText}>From {source}</Text>
          </View>
        )}

        {/* Existing reschedule request notice */}
        {rescheduleData && rescheduleData.status === 'pending' && (
          <View style={styles.existingRequestNotice}>
            <View style={styles.noticeHeader}>
              <MaterialIcons name="schedule" size={20} color="#F59E0B" />
              <Text style={styles.noticeTitle}>Pending Reschedule Request</Text>
            </View>
            <Text style={styles.noticeText}>
              You have a pending reschedule request for {rescheduleData.newDate} at {rescheduleData.newTime}.
              Submitting a new request will replace the existing one.
            </Text>
          </View>
        )}

        {/* Interview Info Card */}
        <View style={styles.interviewCard}>
          <View style={styles.interviewHeader}>
            <Image
              source={interviewDetails.logo || defaultLogo}
              style={styles.companyLogo}
            />
            <View style={styles.interviewHeaderText}>
              <Text style={styles.companyName}>{interviewDetails.company}</Text>
              <Text style={styles.positionName}>{interviewDetails.position}</Text>
            </View>
          </View>

          <View style={styles.interviewDetails}>
            <View style={styles.detailItem}>
              <FontAwesome5 name="calendar-alt" size={16} color="#64748B" />
              <Text style={styles.detailText}>Current: {interviewDetails.date}</Text>
            </View>
            <View style={styles.detailItem}>
              <Ionicons name="time-outline" size={16} color="#64748B" />
              <Text style={styles.detailText}>
                {interviewDetails.time} ({interviewDetails.duration})
              </Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialIcons name="video-call" size={16} color="#64748B" />
              <Text style={styles.detailText}>{interviewDetails.type}</Text>
            </View>
          </View>
        </View>

        {/* Date Selection */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Select New Date</Text>
          <Text style={styles.sectionSubtitle}>
            Choose from available weekdays (Monday - Friday)
          </Text>
          <TouchableOpacity
            style={styles.datePickerButton}
            onPress={toggleCustomDatePicker}
          >
            <FontAwesome5
              name="calendar-alt"
              size={20}
              color="#0e4e96"
              style={styles.datePickerIcon}
            />
            <Text style={styles.datePickerText}>
              {formatDate(selectedDate)}
            </Text>
            <MaterialIcons name="arrow-drop-down" size={24} color="#0e4e96" />
          </TouchableOpacity>

          {/* Custom Date Picker Modal */}
          <CustomDatePicker />
        </View>

        {/* Time Slot Selection */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Select New Time</Text>
          <Text style={styles.sectionSubtitle}>
            Available time slots for {formatDate(selectedDate)}
          </Text>

          {timeSlots.length > 0 ? (
            <View style={styles.timeSlotsContainer}>
              {timeSlots.map(slot => (
                <TouchableOpacity
                  key={slot.id}
                  style={[
                    styles.timeSlot,
                    !slot.available && styles.unavailableTimeSlot,
                    selectedTimeSlot?.id === slot.id && styles.selectedTimeSlot,
                  ]}
                  onPress={() => handleTimeSlotSelect(slot)}
                  disabled={!slot.available}
                >
                  <Text
                    style={[
                      styles.timeSlotText,
                      !slot.available && styles.unavailableTimeSlotText,
                      selectedTimeSlot?.id === slot.id &&
                        styles.selectedTimeSlotText,
                    ]}
                  >
                    {slot.time}
                  </Text>
                  {!slot.available && (
                    <Text style={styles.unavailableText}>Unavailable</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          ) : (
            <View style={styles.noSlotsContainer}>
              <MaterialIcons name="event-busy" size={48} color="#94A3B8" />
              <Text style={styles.noSlotsText}>No available time slots for this date</Text>
              <Text style={styles.noSlotsSubtext}>Please select a different date</Text>
            </View>
          )}
        </View>

        {/* Reason for Rescheduling */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Reason for Rescheduling</Text>
          <Text style={styles.sectionSubtitle}>
            Please select the main reason for your reschedule request
          </Text>

          <View style={styles.reasonsContainer}>
            {rescheduleReasons.map((item, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.reasonItem,
                  reason === item && styles.selectedReasonItem,
                ]}
                onPress={() => handleReasonSelect(item)}
              >
                <View
                  style={[
                    styles.reasonCheckbox,
                    reason === item && styles.selectedReasonCheckbox,
                  ]}
                >
                  {reason === item && (
                    <Ionicons name="checkmark" size={16} color="#ffffff" />
                  )}
                </View>
                <Text
                  style={[
                    styles.reasonText,
                    reason === item && styles.selectedReasonText,
                  ]}
                >
                  {item}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {reason === 'Other (please specify)' && (
            <TextInput
              style={styles.reasonInput}
              placeholder="Please specify your reason..."
              placeholderTextColor="#94A3B8"
              multiline
              numberOfLines={3}
              value={reason !== 'Other (please specify)' ? reason : ''}
              onChangeText={text => setReason(text)}
            />
          )}
        </View>

        {/* Message to Interviewer */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>
            Message to Interviewer (Optional)
          </Text>
          <Text style={styles.sectionSubtitle}>
            Add a personal message explaining your request
          </Text>
          <TextInput
            style={styles.messageInput}
            placeholder="Add a personal message explaining your request..."
            placeholderTextColor="#94A3B8"
            multiline
            numberOfLines={4}
            value={message}
            onChangeText={setMessage}
          />
        </View>

        {/* Interviewers */}
        {interviewDetails?.interviewers && (
          <View style={styles.sectionContainer}>
            <Text style={styles.sectionTitle}>Your Interviewers</Text>

            <View style={styles.interviewersContainer}>
              {interviewDetails.interviewers.map((interviewer, index) => (
                <View key={index} style={styles.interviewerItem}>
                  <View style={styles.interviewerAvatar}>
                    {interviewer.avatar ? (
                      <Image
                        source={interviewer.avatar}
                        style={styles.avatarImage}
                      />
                    ) : (
                      <Text style={styles.avatarText}>
                        {interviewer.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')}
                      </Text>
                    )}
                  </View>
                  <View style={styles.interviewerInfo}>
                    <Text style={styles.interviewerName}>
                      {interviewer.name}
                    </Text>
                    <Text style={styles.interviewerTitle}>
                      {interviewer.title}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Submit Button */}
        <TouchableOpacity
          style={[
            styles.submitButton,
            (!selectedDate || !selectedTimeSlot || !reason) && styles.submitButtonDisabled
          ]}
          onPress={handleSubmit}
          disabled={isSubmitting || !selectedDate || !selectedTimeSlot || !reason}
        >
          {isSubmitting ? (
            <ActivityIndicator size={20} color="#ffffff" />
          ) : (
            <Text style={styles.submitButtonText}>Request Reschedule</Text>
          )}
        </TouchableOpacity>

        {/* Cancellation Note */}
        <Text style={styles.cancellationNote}>
          Note: Rescheduling is subject to availability and approval. You will
          receive a confirmation once your request is processed. Please allow 24-48 hours for a response.
        </Text>
      </ScrollView>

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmationModal}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.successIconContainer}>
              <AntDesign name="checkcircle" size={60} color="#10B981" />
            </View>

            <Text style={styles.modalTitle}>Reschedule Requested</Text>
            <Text style={styles.modalMessage}>
              Your request to reschedule the interview with {interviewDetails?.company}{' '}
              has been submitted successfully.
            </Text>

            <View style={styles.newScheduleContainer}>
              <Text style={styles.newScheduleTitle}>Requested New Schedule:</Text>
              <View style={styles.newScheduleItem}>
                <FontAwesome5 name="calendar-alt" size={16} color="#64748B" />
                <Text style={styles.newScheduleText}>
                  {formatDate(selectedDate)}
                </Text>
              </View>
              <View style={styles.newScheduleItem}>
                <Ionicons name="time-outline" size={16} color="#64748B" />
                <Text style={styles.newScheduleText}>
                  {selectedTimeSlot?.time}
                </Text>
              </View>
              <View style={styles.newScheduleItem}>
                <MaterialIcons name="info-outline" size={16} color="#64748B" />
                <Text style={styles.newScheduleText}>
                  Reason: {reason}
                </Text>
              </View>
            </View>

            <Text style={styles.modalSubMessage}>
              You will receive a notification once the company confirms your new
              interview time. Please check your email and app notifications regularly.
            </Text>

            <TouchableOpacity
              style={styles.modalButton}
              onPress={handleConfirmation}
            >
              <Text style={styles.modalButtonText}>Return to Interviews</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: 16,
  },
  sourceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  sourceText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  existingRequestNotice: {
    backgroundColor: '#FEF3C7',
    borderLeftWidth: 4,
    borderLeftColor: '#F59E0B',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  noticeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  noticeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#92400E',
  },
  noticeText: {
    fontSize: 14,
    color: '#92400E',
    lineHeight: 20,
  },
  interviewCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  interviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  companyLogo: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  interviewHeaderText: {
    flex: 1,
  },
  companyName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  positionName: {
    fontSize: 16,
    color: '#64748B',
  },
  interviewDetails: {
    gap: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  detailText: {
    fontSize: 14,
    color: '#64748B',
  },
  sectionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
    lineHeight: 20,
  },
  datePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 16,
    gap: 12,
  },
  datePickerIcon: {
    marginRight: 4,
  },
  datePickerText: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontWeight: '500',
  },
  timeSlotsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  timeSlot: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    minWidth: 100,
    alignItems: 'center',
  },
  selectedTimeSlot: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  unavailableTimeSlot: {
    backgroundColor: '#F1F5F9',
    borderColor: '#CBD5E1',
  },
  timeSlotText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
  },
  selectedTimeSlotText: {
    color: '#ffffff',
  },
  unavailableTimeSlotText: {
    color: '#94A3B8',
  },
  unavailableText: {
    fontSize: 10,
    color: '#94A3B8',
    marginTop: 2,
  },
  noSlotsContainer: {
    alignItems: 'center',
    padding: 40,
  },
  noSlotsText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 16,
    textAlign: 'center',
  },
  noSlotsSubtext: {
    fontSize: 14,
    color: '#94A3B8',
    marginTop: 4,
    textAlign: 'center',
  },
  reasonsContainer: {
    gap: 12,
  },
  reasonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    gap: 12,
  },
  selectedReasonItem: {
    backgroundColor: '#EBF4FF',
    borderColor: '#0e4e96',
  },
  reasonCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedReasonCheckbox: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  reasonText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
  },
  selectedReasonText: {
    color: '#1E293B',
    fontWeight: '500',
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1E293B',
    textAlignVertical: 'top',
    minHeight: 80,
    marginTop: 12,
  },
  messageInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1E293B',
    textAlignVertical: 'top',
    minHeight: 100,
  },
  interviewersContainer: {
    gap: 12,
  },
  interviewerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  interviewerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  interviewerTitle: {
    fontSize: 14,
    color: '#64748B',
  },
  submitButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  cancellationNote: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 20,
  },
  datePickerModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  datePickerModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  datePickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  datePickerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
  },
  datePickerScrollView: {
    marginBottom: 20,
  },
  datePickerItem: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 12,
    minWidth: 80,
    alignItems: 'center',
  },
  datePickerItemSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  datePickerDayText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    textAlign: 'center',
  },
  datePickerTextSelected: {
    color: '#ffffff',
  },
  datePickerConfirmButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  datePickerConfirmText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  successIconContainer: {
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  newScheduleContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    width: '100%',
    marginBottom: 20,
  },
  newScheduleTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
  },
  newScheduleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 12,
  },
  newScheduleText: {
    fontSize: 14,
    color: '#64748B',
  },
  modalSubMessage: {
    fontSize: 12,
    color: '#94A3B8',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 18,
  },
  modalButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: '100%',
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});

